import Component from '@glimmer/component'
import { action } from '@ember/object'
import { registerDestructor } from '@ember/destroyable'
import animatedIf from 'ember-animated/components/animated-if'
import fadeModal from 'customer-flows/utils/animations/fade-modal'

const FADE_DURATION_MS = 200

interface UiKitModalSignature {
  Args: {
    isOpen: boolean
    headerTitle?: string
    headerText?: string
    noBorder?: boolean
    noPaddingOnBody?: boolean
    stayOpenOnOverlayClick?: boolean
    onClose: () => void
  }
  Blocks: {
    header: []
    body: []
    footer: []
    content: []
  }
  Element: HTMLElement
}

/**
 * wraps iliad's <il-modal>, as an {{in-element}} to document.body
 * and full-viewport below the `sm` breakpoint
 */
export default class UiKitModal extends Component<UiKitModalSignature> {
  constructor(owner: unknown, args: UiKitModalSignature['Args']) {
    super(owner, args)

    document.addEventListener('keydown', this.onKeydown)
    registerDestructor(this, () => document.removeEventListener('keydown', this.onKeydown))
  }

  get destinationElement(): HTMLElement {
    return document.body
  }

  get fadeDuration(): number {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : FADE_DURATION_MS
  }

  @action
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.args.isOpen) this.args.onClose()
  }

  <template>
    {{#in-element this.destinationElement insertBefore=null}}
      {{#animatedIf @isOpen use=fadeModal duration=this.fadeDuration initialInsertion=true}}
        {{! @glint-ignore: wait until we can't declare custom elements in glint }}
        <il-modal
          data-testid='ui-kit__modal'
          role='dialog'
          aria-modal='true'
          aria-label={{@headerTitle}}
          isOpen={{true}}
          headerTitle={{@headerTitle}}
          headerText={{@headerText}}
          noBorder={{@noBorder}}
          noPaddingOnBody={{@noPaddingOnBody}}
          stayOpenOnOverlayClick={{@stayOpenOnOverlayClick}}
          {{! @glint-ignore: wait until we can't declare custom elements in glint }}
          onClose={{@onClose}}
          class='relative z-50
            il-modal-container:z-50
            il-modal:flex il-modal:flex-col
            il-modal-body:min-h-0 il-modal-body:flex-1
            max-sm:il-modal:h-full max-sm:il-modal:w-full
            max-sm:il-modal:max-w-none max-sm:il-modal:max-h-none
            max-sm:il-modal:rounded-none max-sm:il-modal:border-0'
          ...attributes
        >
          {{#if (has-block 'header')}}
            <div slot='header'>
              {{yield to='header'}}
            </div>
          {{/if}}

          {{#if (has-block 'content')}}
            {{yield to='content'}}
          {{else}}
            <div slot='body' class='relative h-full'>
              {{yield to='body'}}
            </div>

            {{#if (has-block 'footer')}}
              <div slot='footer'>
                {{yield to='footer'}}
              </div>
            {{/if}}
          {{/if}}
        </il-modal>
      {{/animatedIf}}
    {{/in-element}}
  </template>
}
